import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";

export default function RegisterPage() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [role, setRole] = useState(params.get("role") || "user");
  const [referralCode, setReferralCode] = useState(params.get("ref") || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{12}$/.test(aadhaar)) {
      toast.error("Aadhaar 12 digits ka hona chahiye");
      return;
    }
    setLoading(true);
    try {
      const user = await register(name, email, password, role, referralCode || null, aadhaar);
      toast.success(referralCode ? `Account created · Referral code applied` : "Account created");
      if (user.role === "hotel_partner") nav("/partner/hotels");
      else if (user.role === "room_partner") nav("/partner/rooms");
      else nav("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Join Zyro</p>
      <h1 className="font-serif text-4xl text-midnight mb-8">Create your account</h1>
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-500 mb-2 block">I am a</Label>
          <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-3 gap-2" data-testid="role-select">
            {[
              { v: "user", label: "Guest / Tenant" },
              { v: "hotel_partner", label: "Hotelier" },
              { v: "room_partner", label: "Room Owner" },
            ].map((r) => (
              <label key={r.v} htmlFor={`role-${r.v}`} className={`cursor-pointer p-3 rounded-xl border text-center text-sm ${
                role === r.v ? "border-gold bg-gold/5 text-midnight" : "border-black/10 text-neutral-500 hover:bg-stone"
              }`}>
                <RadioGroupItem value={r.v} id={`role-${r.v}`} className="sr-only" />
                {r.label}
              </label>
            ))}
          </RadioGroup>
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Full name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" placeholder="Jane Doe" />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Email</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email" placeholder="you@example.com" />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Password</label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="register-password" placeholder="At least 6 characters" />
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">
            Aadhaar Number <span className="text-red-600">*</span>
          </label>
          <Input
            inputMode="numeric"
            maxLength={12}
            value={aadhaar}
            onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))}
            required
            data-testid="register-aadhaar"
            placeholder="12-digit Aadhaar number"
            className="tracking-widest font-mono"
          />
          <p className="text-xs text-neutral-500 mt-1">Aadhaar number is required for account verification & booking safety.</p>
        </div>
        <div>
          <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">
            Referral code <span className="text-neutral-400 normal-case">(optional — get ₹500 off)</span>
          </label>
          <Input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} data-testid="register-referral" placeholder="ZY-XXX1234" className="tracking-widest font-mono" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="register-submit-btn">
          {loading ? "Creating..." : "Create Account"}
        </Button>
      </form>
      <div className="mt-6 space-y-2 text-sm text-neutral-600">
        <p>
          Already have an account?{" "}
          <Link to="/login" className="text-midnight underline hover:text-gold" data-testid="switch-to-login">Sign in</Link>
        </p>
        <p>
          Prefer OTP?{" "}
          <Link to="/otp-login" className="text-midnight underline hover:text-gold" data-testid="switch-to-otp">Sign in with mobile OTP</Link>
        </p>
      </div>
    </div>
  );
}
